import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { merchantsApi, shopsApi, counterpartiesApi } from 'api';
import {
  CloseDialogResult,
  CopyText,
  CrudPage,
  DataGridColumnDefinition,
  dataGridColumns,
  Dialog,
  ExpandableRequisites,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { FilterDefinitionType, QueryKey } from 'enums';
import {
  useMutation,
  usePartialQuery,
  usePayinRequisitesUtils,
  useShopsQuery,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  Counterparty,
  CounterpartiesFilters,
  Requisites,
  CounterpartyRequisites,
} from 'types';
import { formatUtils } from 'utils';

export const CounterpartiesPage: React.FC = () => {
  const { t: tLayout } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'layout',
  });
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.counterparties',
  });
  const { t: tCommon } = useTranslation();
  const { role, isTechOperator, isAdmin } = useUser();
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const queryResult = usePartialQuery(
    QueryKey.Counterparties,
    counterpartiesApi.getAllPaginated,
  );

  const [confirmRemoveDialogProps, setConfirmRemoveDialogProps] = useState<{
    open: boolean;
    data?: Partial<Counterparty>;
  }>({
    open: false,
  });

  const queryResultMerchants = useQuery(
    QueryKey.Merchants,
    merchantsApi.getAllAsRole(role),
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const { mutate: removeCounterparty, isLoading: isRemoveLoading } =
    useMutation(counterpartiesApi.remove, {
      onSuccess: () => {
        setConfirmRemoveDialogProps({ open: false });
        queryClient.invalidateQueries(QueryKey.Counterparties);
      },
    });

  const detailsUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.COUNTERPARTY_DETAILS;
    }
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.COUNTERPARTY_DETAILS;
    }

    return '';
  }, [isTechOperator, isAdmin]);

  const handleCreateOrEdit = useCallback(
    (id: string) => navigate(generatePath(detailsUrl!, { id })),
    [detailsUrl, navigate],
  );

  const handleCloseRemoveDialog = useCallback(
    ({ ok, data }: CloseDialogResult<Counterparty>) => {
      if (ok && data) {
        removeCounterparty(data.id);
      } else {
        setConfirmRemoveDialogProps({ open: false });
      }
    },
    [removeCounterparty],
  );

  const handleRemove = useCallback((data: Counterparty) => {
    setConfirmRemoveDialogProps({ open: true, data });
  }, []);

  const handleEdit = useCallback(
    (data: Counterparty) => handleCreateOrEdit(data.id),
    [handleCreateOrEdit],
  );

  const { handleOpenPage, detailsUrl: requisitesDetailsUrl } =
    usePayinRequisitesUtils();

  const renderExpandableRequisites = useCallback(
    ({ requisitesId, requisites }: CounterpartyRequisites) => (
      <ExpandableRequisites
        key={requisitesId}
        requisites={(requisites || { id: requisitesId }) as Requisites}
        onClick={() => handleOpenPage(requisitesId, requisitesDetailsUrl)}
        readOnly
        showTrader
      />
    ),
    [handleOpenPage, requisitesDetailsUrl],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<Counterparty>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.shop'),
        valueGetter: (item) => (
          <Fragment>
            <div>{item.merchant?.user?.name}</div>
            <div>{formatUtils.formatName(item.shop)}</div>
          </Fragment>
        ),
      },
      {
        header: t('fields.customers'),
        multiValueRenderer: {
          itemsGetter: (item) => item.customers ?? [],
          valueGetter: ({ customerId }: { customerId: string }) => (
            <div className="tw-mb-1">
              <CopyText text={customerId} />
            </div>
          ),
        },
      },
      {
        header: t('fields.requisites'),
        multiValueRenderer: {
          itemsGetter: (item) => item.requisites,
          valueGetter: renderExpandableRequisites,
        },
      },
      {
        header: t('fields.distribution_mode'),
        valueKey: 'requisitesDistributionMode',
        valueFormatter: (value) =>
          t(`distribution_modes.${value}`, { defaultValue: value }),
      },
      dataGridColumns.getActionsColumn({
        handleRemove,
        handleEdit,
      }),
    ],
    [t, handleRemove, handleEdit, renderExpandableRequisites],
  );

  const filtersDefinitions: FilterDefinition<CounterpartiesFilters>[] = useMemo(
    () => [
      {
        label: tCommon('filters.merchant'),
        name: 'merchantId',
        type: FilterDefinitionType.Merchant,
        merchants: queryResultMerchants.data,
      },
      {
        label: tCommon('filters.shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        shops: queryResultShops.data,
      },
      {
        label: tCommon('filters.customer'),
        name: 'customerId',
        type: FilterDefinitionType.Text,
      },
    ],
    [queryResultMerchants.data, queryResultShops.data, tCommon],
  );

  return (
    <Fragment>
      <CrudPage
        header={{
          title: tLayout('menu_items.customers'),
          rightContentButton: {
            onClick: () => handleCreateOrEdit(NEW_ID),
          },
        }}
        filters={{ filtersDefinitions }}
        table={{ queryResult, columns, paginated: true }}
      >
        <Dialog
          title={t('remove_dialog.title')}
          onClose={handleCloseRemoveDialog}
          disabled={isRemoveLoading}
          {...confirmRemoveDialogProps}
        >
          <Fragment>
            <div className="tw-mb-4">{t('remove_dialog.description')}</div>
            {confirmRemoveDialogProps.data && (
              <Fragment>
                <div className="tw-mb-2">
                  <div>
                    {confirmRemoveDialogProps.data.merchant?.user?.name}
                  </div>
                  <div>
                    {formatUtils.formatName(confirmRemoveDialogProps.data.shop)}
                  </div>
                </div>
              </Fragment>
            )}
          </Fragment>
        </Dialog>
      </CrudPage>
    </Fragment>
  );
};
